import type { ServerVersion } from "./radar-connection";

const LAST_REFRESH_KEY = "meeting-radar:last-hard-refresh-at";
const WORKER_VERSION_KEY = "meeting-radar:last-worker-version";
const RELOAD_LATCH_KEY = "meeting-radar:reload-latch";
const HOUR_MS = 60 * 60 * 1_000;

export type HardRefreshEnvironment = {
  storage: Pick<Storage, "getItem" | "setItem">;
  readFallbackLatch(): string | null;
  writeFallbackLatch(value: string): void;
  reload(signature: string): void;
  now(): number;
  isOnline(): boolean;
  isVisible(): boolean;
  addWindowListener(type: "online" | "focus" | "pageshow", listener: () => void): void;
  removeWindowListener(type: "online" | "focus" | "pageshow", listener: () => void): void;
  addVisibilityListener(listener: () => void): void;
  removeVisibilityListener(listener: () => void): void;
};

export type HardRefreshOptions = {
  environment: HardRefreshEnvironment;
  loadedGitCommit: string;
  isDeployEligible: () => boolean;
  isHourlyEligible: () => boolean;
  setTimeout?: typeof globalThis.setTimeout;
  clearTimeout?: typeof globalThis.clearTimeout;
};

type Timer = ReturnType<typeof globalThis.setTimeout>;
type FallbackLatch = { signature: string; refreshedAt: number | null };

export function navigationReloadToken(signature: string, refreshedAt: number): string {
  return `${refreshedAt.toString(36)}.${signature}`;
}

export function parseNavigationReloadToken(token: string | null): FallbackLatch | null {
  if (!token) return null;
  const separator = token.indexOf(".");
  if (separator < 1) return null;
  const encodedTimestamp = token.slice(0, separator);
  if (!/^[0-9a-z]+$/.test(encodedTimestamp)) return null;
  const refreshedAt = Number.parseInt(encodedTimestamp, 36);
  const signature = token.slice(separator + 1);
  return signature && Number.isFinite(refreshedAt) ? { signature, refreshedAt } : null;
}

export class HardRefreshManager {
  private readonly schedule: typeof globalThis.setTimeout;
  private readonly cancel: typeof globalThis.clearTimeout;
  private active = false;
  private reloadTriggered = false;
  private timer: Timer | null = null;
  private pendingDeploySignature: string | null = null;
  private readonly memory = new Map<string, string>();

  constructor(private readonly options: HardRefreshOptions) {
    this.schedule = options.setTimeout ?? globalThis.setTimeout.bind(globalThis);
    this.cancel = options.clearTimeout ?? globalThis.clearTimeout.bind(globalThis);
  }

  start(): void {
    if (this.active) return;
    this.active = true;
    const { environment } = this.options;
    environment.addWindowListener("online", this.evaluate);
    environment.addWindowListener("focus", this.evaluate);
    environment.addWindowListener("pageshow", this.evaluate);
    environment.addVisibilityListener(this.evaluate);
    this.evaluate();
    this.scheduleNextCheck();
  }

  stop(): void {
    if (!this.active) return;
    this.active = false;
    const { environment } = this.options;
    environment.removeWindowListener("online", this.evaluate);
    environment.removeWindowListener("focus", this.evaluate);
    environment.removeWindowListener("pageshow", this.evaluate);
    environment.removeVisibilityListener(this.evaluate);
    if (this.timer !== null) this.cancel(this.timer);
    this.timer = null;
  }

  observeServerVersion(version: ServerVersion): void {
    if (!this.active || this.reloadTriggered) return;
    const previousWorkerVersion = this.read(WORKER_VERSION_KEY);
    this.write(WORKER_VERSION_KEY, version.cloudflareWorkerVersion);

    const gitChanged = Boolean(version.gitCommit && version.gitCommit !== this.options.loadedGitCommit);
    const workerChanged = previousWorkerVersion !== null && previousWorkerVersion !== version.cloudflareWorkerVersion;
    if (!gitChanged && !workerChanged) return;

    const signature = `deploy:${version.gitCommit}:${version.cloudflareWorkerVersion}`;
    if (this.hasLatch(signature)) return;
    this.pendingDeploySignature = signature;
    this.evaluate();
  }

  readonly evaluate = (): void => {
    if (!this.active || this.reloadTriggered) return;
    const { environment } = this.options;
    if (!environment.isOnline() || !environment.isVisible()) return;
    if (this.pendingDeploySignature && this.options.isDeployEligible()) {
      this.triggerReload(this.pendingDeploySignature);
      return;
    }
    if (!this.options.isHourlyEligible()) return;
    const now = environment.now();
    const baseline = this.readRefreshBaseline(now);
    if (baseline === null || baseline > now) {
      this.write(LAST_REFRESH_KEY, String(now));
      return;
    }
    if (now - baseline < HOUR_MS) return;
    this.triggerReload(`hourly:${now}`);
  };

  private triggerReload(signature: string): void {
    this.reloadTriggered = true;
    this.write(LAST_REFRESH_KEY, String(this.options.environment.now()));
    this.write(RELOAD_LATCH_KEY, signature);
    try {
      this.options.environment.writeFallbackLatch(JSON.stringify({
        signature,
        refreshedAt: this.options.environment.now()
      } satisfies FallbackLatch));
    } catch { /* Reload still remains latched in memory. */ }
    this.options.environment.reload(signature);
  }

  private scheduleNextCheck(): void {
    if (!this.active) return;
    const baseline = this.readRefreshBaseline(this.options.environment.now());
    const remaining = baseline === null ? 60_000 : HOUR_MS - (this.options.environment.now() - baseline);
    const delay = remaining > 0 ? Math.min(HOUR_MS, remaining) : 60_000;
    this.timer = this.schedule(() => {
      this.timer = null;
      this.evaluate();
      this.scheduleNextCheck();
    }, delay);
  }

  private read(key: string): string | null {
    try {
      const value = this.options.environment.storage.getItem(key);
      if (value !== null) this.memory.set(key, value);
      return value ?? this.memory.get(key) ?? null;
    } catch { return this.memory.get(key) ?? null; }
  }

  private readTimestamp(key: string): number | null {
    const value = this.read(key);
    if (value === null) return null;
    const timestamp = Number(value);
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  private hasLatch(signature: string): boolean {
    if (this.read(RELOAD_LATCH_KEY) === signature) return true;
    return this.readFallbackLatch()?.signature === signature;
  }

  private readRefreshBaseline(now: number): number | null {
    const candidates = [
      this.readTimestamp(LAST_REFRESH_KEY),
      this.readFallbackLatch()?.refreshedAt ?? null
    ].filter((value): value is number => value !== null && value <= now);
    return candidates.length ? Math.max(...candidates) : null;
  }

  private readFallbackLatch(): FallbackLatch | null {
    let raw: string | null;
    try { raw = this.options.environment.readFallbackLatch(); } catch { return null; }
    if (raw === null) return null;
    try {
      const parsed = JSON.parse(raw) as Partial<FallbackLatch>;
      if (typeof parsed.signature === "string") {
        return {
          signature: parsed.signature,
          refreshedAt: typeof parsed.refreshedAt === "number" && Number.isFinite(parsed.refreshedAt)
            ? parsed.refreshedAt : null
        };
      }
    } catch {
      // Plain signatures were written by earlier builds.
    }
    const hourlyTimestamp = raw.startsWith("hourly:") ? Number(raw.slice("hourly:".length)) : NaN;
    return { signature: raw, refreshedAt: Number.isFinite(hourlyTimestamp) ? hourlyTimestamp : null };
  }

  private write(key: string, value: string): void {
    this.memory.set(key, value);
    try { this.options.environment.storage.setItem(key, value); } catch { /* Storage can be unavailable in privacy modes. */ }
  }
}

export function browserHardRefreshEnvironment(): HardRefreshEnvironment {
  const historyKey = "meetingRadarReloadLatch";
  return {
    storage: sessionStorage,
    readFallbackLatch: () => {
      const state = history.state as Record<string, unknown> | null;
      if (typeof state?.[historyKey] === "string") return state[historyKey];
      const navigationLatch = parseNavigationReloadToken(new URL(location.href).searchParams.get("_mr_reload"));
      return navigationLatch ? JSON.stringify(navigationLatch) : null;
    },
    writeFallbackLatch: (value) => {
      const state = typeof history.state === "object" && history.state !== null ? history.state as Record<string, unknown> : {};
      history.replaceState({ ...state, [historyKey]: value }, "", location.href);
    },
    reload: (signature) => {
      const url = new URL(location.href);
      url.searchParams.set("_mr_reload", navigationReloadToken(signature, Date.now()));
      location.replace(url.href);
    },
    now: Date.now,
    isOnline: () => navigator.onLine,
    isVisible: () => document.visibilityState === "visible",
    addWindowListener: (type, listener) => window.addEventListener(type, listener),
    removeWindowListener: (type, listener) => window.removeEventListener(type, listener),
    addVisibilityListener: (listener) => document.addEventListener("visibilitychange", listener),
    removeVisibilityListener: (listener) => document.removeEventListener("visibilitychange", listener)
  };
}
